// src/components/admin/page-management/templates/AboutUsTemplate.js

const AboutUsTemplate = {
    title: "About Us",
    description: "A complete about page with company story, values, mission, and timeline",
    sections: [
      // Slideshow Hero Section
      {
        type: 'hero',
        content: {
          title: 'Transforming Packaging, Elevating Brands',
          subtitle: 'SINCE 1998',
          text: 'Expert packaging solutions that enhance your brand identity and make a lasting impression',
          imageUrl: '/images/s3.jpg',  // First image from the slideshow
          buttonText: 'Learn More',
          buttonLink: '#learn-more',
          buttonStyle: 'primary',
          alignment: 'center',
          backgroundColor: 'rgba(0,0,0,0.6)',
          textColor: '#ffffff'
        },
        settings: {
          fullWidth: true,
          customCss: 'height: 800px; overflow: hidden;',
          animation: 'fade-in'
        }
      },
      // Introduction Section
      {
        type: 'container',
        content: {
          containerType: 'standard',
          backgroundColor: '#f9fafb',
          padding: '4rem 0'
        },
        settings: {
          fullWidth: true,
          containerClass: 'container mx-auto px-4 py-16 space-y-16'
        }
      },
      // About Box Section (nested inside the container)
      {
        type: 'richText',
        content: {
          richText: `<div class="bg-white shadow-xl rounded-2xl p-12 space-y-8 transform hover:shadow-2xl transition-all duration-300">
            <h1 class="text-4xl font-extrabold text-gray-900 text-center mb-8 relative">
              About Bag&Box
              <div class="h-1 w-24 bg-amber-700 mx-auto mt-4"></div>
            </h1>
            <div class="grid md:grid-cols-2 gap-10">
              <div class="space-y-6">
                <p class="text-lg text-gray-700 leading-relaxed">
                  At Bag&Box, we are more than just a packaging company—we are your strategic branding partner. Specializing in high-quality, customizable packaging solutions, we serve businesses across North America with innovative printing technologies.
                </p>
                <p class="text-lg text-gray-700 leading-relaxed">
                  Our state-of-the-art platform allows you to transform ordinary packaging into extraordinary brand statements, with vibrant full-color printing available even for small order quantities.
                </p>
              </div>
              <div class="space-y-6">
                <p class="text-lg text-gray-700 leading-relaxed">
                  From shopping bags and pizza boxes to cup holders, we offer versatile products in white and kraft brown. Our user-friendly online platform at bagbox.ca makes customization simple and intuitive.
                </p>
                <p class="text-lg text-gray-700 leading-relaxed">
                  Whether you're a startup or an established enterprise, we provide professional packaging solutions that help you stand out in a competitive market.
                </p>
              </div>
            </div>
          </div>`,
          backgroundColor: 'transparent'
        },
        settings: {
          fullWidth: false,
          containerClass: 'my-16'
        },
        parentId: '[CONTAINER_ID]' // This will be dynamically replaced when applied
      },
      // Why Partner Section (nested inside the container)
      {
        type: 'richText',
        content: {
          richText: `<section class="relative bg-white shadow-xl rounded-2xl p-16 space-y-10 overflow-hidden">
            <div class="absolute top-0 right-0 w-64 h-64 bg-[#033568]/10 rounded-full transform translate-x-1/3 -translate-y-1/3 z-0"></div>
            <div class="absolute bottom-0 left-0 w-64 h-64 bg-amber-700/10 rounded-full transform -translate-x-1/3 translate-y-1/3 z-0"></div>
            
            <h2 class="text-3xl font-bold text-gray-900 text-center mb-6 relative z-10">
              Why Partner with Bag&Box?
              <div class="h-1 w-24 bg-amber-700 mx-auto mt-4"></div>
            </h2>
            <div class="grid md:grid-cols-3 gap-8 relative z-10">
              <div class="text-center p-8 bg-gray-50 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-md mb-6">
                  <svg class="w-14 h-14 text-[#033568] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 class="text-2xl font-semibold text-gray-900 mb-4">Proven Expertise</h3>
                <p class="text-gray-600 leading-relaxed">With decades of design and printing experience since 1998, we bring unparalleled knowledge to every project.</p>
              </div>
              
              <div class="text-center p-8 bg-gray-50 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-md mb-6">
                  <svg class="w-14 h-14 text-amber-700 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 class="text-2xl font-semibold text-gray-900 mb-4">Flexible Solutions</h3>
                <p class="text-gray-600 leading-relaxed">Low minimum orders make professional packaging accessible to businesses of all sizes.</p>
              </div>
              
              <div class="text-center p-8 bg-gray-50 rounded-xl hover:shadow-lg transition-all duration-300 transform hover:-translate-y-2 border border-gray-100">
                <div class="inline-flex items-center justify-center w-20 h-20 rounded-full bg-white shadow-md mb-6">
                  <svg class="w-14 h-14 text-[#033568] mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <h3 class="text-2xl font-semibold text-gray-900 mb-4">Speed & Value</h3>
                <p class="text-gray-600 leading-relaxed">Competitive pricing and quick turnaround times ensure your packaging needs are met efficiently.</p>
              </div>
            </div>
          </section>`,
          backgroundColor: 'transparent'
        },
        settings: {
          fullWidth: false,
          containerClass: 'my-16'
        },
        parentId: '[CONTAINER_ID]' // This will be dynamically replaced when applied
      },
      // Mission Statement (nested inside the container)
      {
        type: 'richText',
        content: {
          richText: `<section class="bg-gradient-to-br from-[#033568] to-[#01254A] text-white rounded-2xl p-16 text-center shadow-xl transform hover:shadow-2xl transition-all duration-300 relative overflow-hidden">
            <div class="absolute top-0 right-0 w-80 h-80 bg-white/5 rounded-full blur-3xl transform translate-x-1/3 -translate-y-1/3"></div>
            <div class="absolute bottom-0 left-0 w-80 h-80 bg-amber-700/10 rounded-full blur-3xl transform -translate-x-1/3 translate-y-1/3"></div>
            
            <h2 class="text-4xl font-bold mb-8 relative">
              Our Mission
              <div class="h-1 w-24 bg-amber-700 mx-auto mt-4"></div>
            </h2>
            <p class="text-xl max-w-4xl mx-auto leading-relaxed mb-10 text-gray-100">
              To empower businesses by transforming packaging into a powerful marketing tool. We believe every package tells a story—let us help you tell yours with professional, eye-catching design and unmatched quality.
            </p>
            <a 
              href="/contactUs" 
              class="inline-flex items-center bg-amber-700 text-white hover:bg-amber-600 font-bold py-4 px-10 rounded-full transition-all duration-300 transform hover:scale-105 shadow-lg hover:shadow-xl"
            >
              Start Your Project
              <svg class="ml-2 w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                <path fill-rule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clip-rule="evenodd" />
              </svg>
            </a>
          </section>`,
          backgroundColor: 'transparent'
        },
        settings: {
          fullWidth: false,
          containerClass: 'my-16'
        },
        parentId: '[CONTAINER_ID]' // This will be dynamically replaced when applied
      },
      // Company Timeline Section (nested inside the container)
      {
        type: 'richText',
        content: {
          richText: `<section class="bg-white shadow-xl rounded-2xl p-16 space-y-8">
            <h2 class="text-3xl font-bold text-gray-900 text-center mb-6 relative">
              Our Journey
              <div class="h-1 w-24 bg-amber-700 mx-auto mt-4"></div>
            </h2>
            <div class="relative">
              <div class="absolute left-1/2 transform -translate-x-1/2 h-full w-1 bg-[#033568]/10"></div>
              
              <div class="relative flex justify-end mb-12">
                <div class="w-5/12 text-right pr-12">
                  <span class="block text-amber-700 text-3xl font-bold mb-2">1998</span>
                  <h3 class="text-xl font-semibold text-gray-800 mb-2">Founded</h3>
                  <p class="text-gray-600">Bag&Box was established with a vision to revolutionize packaging solutions</p>
                </div>
                <div class="absolute left-1/2 transform -translate-x-1/2 -translate-y-1 w-6 h-6 rounded-full bg-[#033568] border-4 border-white z-10"></div>
              </div>
              
              <div class="relative flex justify-start mb-12">
                <div class="w-5/12 text-left pl-12">
                  <span class="block text-amber-700 text-3xl font-bold mb-2">2005</span>
                  <h3 class="text-xl font-semibold text-gray-800 mb-2">Expansion</h3>
                  <p class="text-gray-600">Expanded our product line to include a wide range of customizable packaging options</p>
                </div>
                <div class="absolute left-1/2 transform -translate-x-1/2 -translate-y-1 w-6 h-6 rounded-full bg-[#033568] border-4 border-white z-10"></div>
              </div>
              
              <div class="relative flex justify-end mb-12">
                <div class="w-5/12 text-right pr-12">
                  <span class="block text-amber-700 text-3xl font-bold mb-2">2012</span>
                  <h3 class="text-xl font-semibold text-gray-800 mb-2">Innovation</h3>
                  <p class="text-gray-600">Introduced cutting-edge printing technologies for vibrant full-color designs</p>
                </div>
                <div class="absolute left-1/2 transform -translate-x-1/2 -translate-y-1 w-6 h-6 rounded-full bg-[#033568] border-4 border-white z-10"></div>
              </div>
              
              <div class="relative flex justify-start mb-12">
                <div class="w-5/12 text-left pl-12">
                  <span class="block text-amber-700 text-3xl font-bold mb-2">2018</span>
                  <h3 class="text-xl font-semibold text-gray-800 mb-2">Digital Transformation</h3>
                  <p class="text-gray-600">Launched our user-friendly online platform for seamless ordering</p>
                </div>
                <div class="absolute left-1/2 transform -translate-x-1/2 -translate-y-1 w-6 h-6 rounded-full bg-[#033568] border-4 border-white z-10"></div>
              </div>
              
              <div class="relative flex justify-end mb-12">
                <div class="w-5/12 text-right pr-12">
                  <span class="block text-amber-700 text-3xl font-bold mb-2">2023</span>
                  <h3 class="text-xl font-semibold text-gray-800 mb-2">Today</h3>
                  <p class="text-gray-600">Serving thousands of businesses across North America with premium packaging solutions</p>
                </div>
                <div class="absolute left-1/2 transform -translate-x-1/2 -translate-y-1 w-6 h-6 rounded-full bg-[#033568] border-4 border-white z-10"></div>
              </div>
            </div>
          </section>`,
          backgroundColor: 'transparent'
        },
        settings: {
          fullWidth: false,
          containerClass: 'my-16'
        },
        parentId: '[CONTAINER_ID]' // This will be dynamically replaced when applied
      },
      // CSS Animations
      {
        type: 'richText',
        content: {
          richText: `<style>
            @keyframes fadeIn {
              from { opacity: 0; }
              to { opacity: 1; }
            }
            
            @keyframes slideUp {
              from { opacity: 0; transform: translateY(20px); }
              to { opacity: 1; transform: translateY(0); }
            }
            
            .animate-fadeIn {
              animation: fadeIn 1s ease-out forwards;
            }
            
            .animate-slideUp {
              animation: slideUp 0.8s ease-out forwards;
            }
            
            .animation-delay-200 {
              animation-delay: 0.2s;
            }
            
            .animation-delay-400 {
              animation-delay: 0.4s;
            }
          </style>`,
          backgroundColor: 'transparent'
        },
        settings: {
          fullWidth: true
        }
      },
      // SEO Metadata
      {
        type: 'richText',
        content: {
          richText: `
          <script type="application/ld+json">
          {
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "Bag&Box Packaging Solutions",
            "description": "Custom packaging solutions provider with over 25 years of expertise in branded packaging.",
            "foundingDate": "1998",
            "url": "https://bagbox.ca/about",
            "founder": {
              "@type": "Person",
              "name": "John Smith"
            }
          }
          </script>
          
          <script>
            document.addEventListener('DOMContentLoaded', function() {
              // Set SEO Metadata
              document.title = 'About Bag&Box | Our Story & Mission';
              
              // Add meta description
              let descMeta = document.querySelector('meta[name="description"]');
              if (!descMeta) {
                descMeta = document.createElement('meta');
                descMeta.setAttribute('name', 'description');
                document.head.appendChild(descMeta);
              }
              descMeta.setAttribute('content', 'Learn about Bag&Box\'s journey since 1998. We provide custom packaging solutions with innovative printing technologies and low minimum orders.');
              
              // Add meta keywords
              let keywordsMeta = document.querySelector('meta[name="keywords"]');
              if (!keywordsMeta) {
                keywordsMeta = document.createElement('meta');
                keywordsMeta.setAttribute('name', 'keywords');
                document.head.appendChild(keywordsMeta);
              }
              keywordsMeta.setAttribute('content', 'custom packaging company, about bag and box, packaging solutions, printing expertise, brand packaging');
              
              // Open Graph Tags
              const ogTags = [
                { property: 'og:title', content: 'About Bag&Box | Our Story & Mission' },
                { property: 'og:description', content: 'Learn about our journey in providing innovative packaging solutions since 1998.' },
                { property: 'og:type', content: 'website' },
                { property: 'og:url', content: window.location.href }
              ];
              
              ogTags.forEach(tag => {
                let metaTag = document.querySelector(\`meta[property="\${tag.property}"]\`);
                if (!metaTag) {
                  metaTag = document.createElement('meta');
                  metaTag.setAttribute('property', tag.property);
                  document.head.appendChild(metaTag);
                }
                metaTag.setAttribute('content', tag.content);
              });
            });
          </script>`,
          backgroundColor: 'transparent'
        },
        settings: {
          fullWidth: true
        }
      }
    ]
  };
  
  export default AboutUsTemplate;